import Axios, {
  AxiosInstance,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  Method,
  ResponseType
} from "axios";

Axios.defaults.withCredentials = true;

export interface IApiService {
  // Any should be removed
  get: <T = any>(requestParams: IRequestMethodParams<undefined>) => Promise<AxiosResponse<T>>;
  post: <T = any, P extends object = {}>(requestParams: IRequestMethodParams<P>) => Promise<AxiosResponse<T>>;
  put: <T = any, P extends object = {}>(requestParams: IRequestMethodParams<P>) => Promise<AxiosResponse<T>>;
  patch: <T = any, P extends object = {}>(requestParams: IRequestMethodParams<P>) => Promise<AxiosResponse<T>>;
  // delete: <T = any>(requestParams: IRequestMethodParams<undefined>) => Promise<AxiosResponse<T>>;
  delete: <T = any>(requestParams: IRequestMethodParams<any>) => Promise<AxiosResponse<T>>;
}

export interface IRequestMethodParams<P> extends Pick<AxiosRequestConfig, "headers"> {
  url: string;
  params?: object;
  data?: P;
  responseType?: ResponseType;
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

export type ApiOnFulfilled<V> = <T = V>(value: V) => T | Promise<T>;
export type ApiOnRejected = (error: any) => any;

export type PagedDataroomFilesInterceptor<V> = (
  onFulfilled?: ApiOnFulfilled<V>,
  onRejected?: ApiOnRejected
) => number | Promise<number>;

export interface ApiServiceParams extends AxiosRequestConfig {
  resIntercept?: {
    onFulfilled?: (value: AxiosResponse) => AxiosResponse;
    onRejected?: (error: any) => any;
  };
  reqIntercept?: {
    onFulfilled: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig;
    onRejected?: (error: any) => any;
  };
}

interface IRequestParamsWithMethod<P> extends IRequestMethodParams<P> {
  method: Method;
}

export class ApiService implements IApiService {
  protected readonly axios: AxiosInstance;

  // constructor({ resIntercept, reqIntercept, ...config }: ApiServiceParams) {
  constructor({ resIntercept, reqIntercept, ...config }: ApiServiceParams) {
    this.axios = Axios.create(config);
    if (resIntercept) {
      this.axios.interceptors.response.use(resIntercept.onFulfilled, resIntercept.onRejected);
    }

    if (reqIntercept) {
      this.axios.interceptors.request.use(reqIntercept.onFulfilled, reqIntercept.onRejected);
    }
  }

  async get<T>(requestParams: IRequestMethodParams<undefined>): Promise<AxiosResponse> {
    return this.request<undefined, T>({ method: "get", ...requestParams });
  }

  async post<P, T>(requestParams: IRequestMethodParams<P>): Promise<AxiosResponse> {
    return this.request<P, T>({ method: "post", ...requestParams });
  }

  async put<P, T>(requestParams: IRequestMethodParams<P>): Promise<AxiosResponse> {
    return this.request<P, T>({ method: "put", ...requestParams });
  }

  async patch<P, T>(requestParams: IRequestMethodParams<P>): Promise<AxiosResponse> {
    return this.request<P, T>({ method: "patch", ...requestParams });
  }

  async delete<T>(requestParams: IRequestMethodParams<undefined>): Promise<AxiosResponse> {
    return this.request<undefined, T>({ method: "delete", ...requestParams });
  }

  private async request<P, T>(requestParams: IRequestParamsWithMethod<P>): Promise<AxiosResponse> {
    const {
      data,
      headers = {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      ...rest
    } = requestParams;
    const response: AxiosResponse<T> = await this.axios.request({
      data,
      headers,
      ...rest
    });
    return response;
  }
}

export default ApiService;
